// Central icon component for catchup-app-web (the learner app, ages 14–18).
//
// Same public API as the admin tool's <Icon>: string-keyed, sized by a named
// scale or pixels, colored via `currentColor` by default. The difference is the
// artwork — app-web uses an age-appropriate glyph pack (see ./packs), not the
// admin tool's bespoke icons.
//
//   <Icon name="dashboard" />                       // resolves via ALIASES → house
//   <Icon name="dashboard-selected" size="small" /> // Fill weight for active nav
//   <Icon name="remove-red" />                        // trash, tinted red by default
//   <Icon name="trash" color="#fff" size={14} />     // canonical name + explicit color
//   <Icon name="/icons/<name>.webp" />               // legacy path still resolves
//
// `normalize()` strips any leading directory (e.g. `/icons/`, `/backgrounds/`) and a
// trailing `.webp`, so path-based call sites resolve cleanly to the named glyph.

import { CSSProperties, memo, SVGProps } from "react";
import { IconName, ALIASES } from "./iconNames";
import { AgePack, getPack } from "./packs";

// Named size scale → pixels (rem-equivalent at the default
// 16px root), so a sized icon keeps the same box as the old raster image.
const SIZE_MAP: Record<string, number> = {
  xsmall: 16,
  small: 24,
  medium: 32,
  large: 40,
  xlarge: 48,
  "2xlarge": 56,
  "3xlarge": 64,
  "4xlarge": 80,
  "5xlarge": 96,
  "6xlarge": 112,
};

export type IconSize = number | keyof typeof SIZE_MAP;

export interface IconProps
  extends Omit<SVGProps<SVGSVGElement>, "name" | "color"> {
  name: IconName | string;
  size?: IconSize;
  color?: string;
  className?: string;
  style?: CSSProperties;
  /** Override the age-group pack for this icon. Defaults to the app default. */
  pack?: AgePack;
}

const normalize = (name: IconName | string): string =>
  typeof name === "string"
    ? name.replace(/^.*\//, "").replace(/\.webp$/, "")
    : name;

const resolve = (
  rawName: IconName | string
): { canonical: IconName | null; defaultColor?: string } => {
  const name = normalize(rawName);
  const alias = ALIASES[name];
  if (alias) {
    return { canonical: alias.name, defaultColor: alias.defaultColor };
  }
  // Allow a canonical name to be passed directly; the pack lookup below decides
  // whether it actually exists (unknown → renders nothing).
  return { canonical: name as IconName };
};

// True when `name` resolves to a real glyph in the (optionally given) pack.
// Useful where a string may be either a migrated glyph name or a path to a
// raster asset that has not been converted — render <Icon> when true, else
// fall back to a raw <img>.
export const iconExists = (name: IconName | string, pack?: AgePack): boolean => {
  const { canonical } = resolve(name);
  return !!(canonical && getPack(pack)[canonical]);
};

const Icon = ({
  name,
  size = 16,
  color,
  style,
  pack,
  ...rest
}: IconProps) => {
  const { canonical, defaultColor } = resolve(name);
  if (!canonical) return null;
  const Glyph = getPack(pack)[canonical];
  if (!Glyph) return null;
  const px = typeof size === "number" ? size : SIZE_MAP[size] ?? 16;
  const finalColor = color ?? defaultColor;
  return (
    <Glyph
      size={px}
      color={finalColor}
      style={{ display: "inline-block", verticalAlign: "middle", ...style }}
      {...(rest as any)}
    />
  );
};

// Memoized: an <Icon> is pure given its props, and the same icon renders
// many times across lists/cards. Without memo, any parent re-render reconciles
// every icon's glyph subtree again; memo lets each icon skip re-render when its
// props are unchanged (the common case for static list/card icons).
export default memo(Icon);
